import { Drawer as ChakraDrawer, DrawerProps } from '@chakra-ui/react'

import { useAppSelector } from '@/redux/hooks'
import { selectVideoWallIsOpen } from '@/redux/videoWall/videoWallSlice'

/**
 * App-wide Drawer. Behaves exactly like Chakra's Drawer, except it yields its
 * focus trap while the full-screen Video Wall overlay is open.
 *
 * The Video Wall (features/video-wall) renders as a sibling of every drawer at
 * the app root and sits above them (z-index 2000). Chakra's default focus trap
 * (`trapFocus` + `useInert`) is DOM-tree aware, not z-index aware, so an open
 * drawer steals focus from the wall and makes its inputs unclickable. While the
 * wall is open we disable trapFocus/useInert/closeOnEsc so the wall owns focus
 * and Escape.
 *
 * Any value a caller passes for these props is AND-composed with the wall state,
 * so per-drawer overrides (e.g. DevicesFilterDrawer's info-modal case) still
 * apply. Import this instead of Chakra's Drawer everywhere (enforced by ESLint).
 */
export const Drawer = (props: DrawerProps) => {
  const isVideoWallOpen = useAppSelector(selectVideoWallIsOpen)
  const isFocusTrapEnabled = !isVideoWallOpen

  return (
    <ChakraDrawer
      {...props}
      closeOnEsc={(props.closeOnEsc ?? true) && isFocusTrapEnabled}
      trapFocus={(props.trapFocus ?? true) && isFocusTrapEnabled}
      useInert={(props.useInert ?? true) && isFocusTrapEnabled}
    />
  )
}
